/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { AvatarProps } from '@coursera/cds-core';
import { Avatar } from '@coursera/cds-core';
import { CameraIcon } from '@coursera/cds-icons';

import TrackedButton from 'bundles/page/components/TrackedButton';

import _t from 'i18n!nls/account-profile';

const DEFAULT_PROFILE_PHOTO_SIZE = 120;

type CdsSize = AvatarProps['size'];

type ProfileImageProps = {
  fullName: string; // Used to generate a fallback image
  image?: string;
  size?: CdsSize;
  onClick?: () => void;
};

const styles = {
  imgHover: () => css`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--cds-color-grey-975);
    color: var(--cds-color-white-0);
    opacity: 0;
    transition: all 0.2s ease;
    border-radius: 50%;

    &:hover {
      opacity: 0.7;
    }
  `,
  defaultImgHover: css`
    &:hover {
      opacity: 1;
    }
  `,
  btn: css`
    position: relative;
    background: transparent;
    border: none;
    border-radius: 50%;
    padding: 0;
  `,
};

type ImageWrapperProps = {
  children: React.ReactNode;
  onClick?: () => void;
  size: CdsSize;
};

const ImageWrapper = ({ children, onClick, ...props }: ImageWrapperProps) => {
  return onClick ? (
    <TrackedButton
      css={styles.btn}
      type="button"
      aria-label={_t('Change photo')}
      onClick={onClick}
      // not tracked in eventing v3
      trackingName="profile_change_profile_photo"
      requireFullyVisible={false}
      withVisibilityTracking={true}
      {...props}
    >
      {children}
    </TrackedButton>
  ) : (
    <div {...props}>{children}</div>
  );
};

const ProfileImage = ({ fullName, image, size = DEFAULT_PROFILE_PHOTO_SIZE, onClick }: ProfileImageProps) => {
  return (
    // Images cannot be masked.
    <div className="fs-exclude">
      <ImageWrapper onClick={onClick} size={size}>
        <Avatar
          variant="user"
          showBorder
          initial={fullName?.[0]}
          imgProps={{
            src: image,
            alt: _t('Profile photo'),
          }}
          size={size}
        />
        {onClick && (
          <div css={[styles.imgHover, image ? null : styles.defaultImgHover]}>
            <CameraIcon />
          </div>
        )}
      </ImageWrapper>
    </div>
  );
};

export default ProfileImage;
